// NATIVE
import * as React from 'react';

// INSTALLED
import { Formik, Form } from 'formik';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// CUSTOM
import { useDispatch } from 'react-redux';
import { SnackAlert } from '../../../components/SnackAlert';
import validationSchema from '../form-models/crear/validationSchema';
import formInitialValues from '../form-models/crear/formInitialValues';
import { closeSessionGlobal, hasPemissions } from '../../../commons/utils';
import { storeCreateTipoCotizacion } from '../../../store/tipo-cotizacion';
import { ModalSessionExpired } from '../../../components/generics/ModalSessionExpired';
import createTipoCotizacionFormModel from '../form-models/crear/createCategoriaCotizacionFormModel';
import { CreateCategoriaCotizacionForm } from '../forms';
import { setCategoriaCotizacionesStore } from '../../../store/categoria-cotizacion';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

/**
 * CreateDialogParameter component renders a dialog for creating a parameter.
 *
 * @param {object} props - The properties passed to the component.
 * @param {boolean} props.open - Determines if the dialog is open.
 * @param {function} props.handleCloseDialog- Function to handle closing the dialog.
 * @param {boolean} [props.buttons=false] - Optional prop to determine if buttons should be displayed.
 *
 * @returns {JSX.Element} The rendered CreateDialogParameter component.
 *
 * @example
 * <CreateDialogParameter
 *   open={true}
 *   handleCloseDialog={handleClose}
 *   buttons={true}
 * />
 *
 * @remarks
 * This component uses Formik for form handling and validation, and Redux for state management.
 * It includes various state hooks for managing form submission, alerts, and session expiration.
 * The component also handles permission validation and response validation for creating a parameter.
 */
export const CreateDialogCategoriaCotizacion = (props: any) => {
  // USE DISPATCH
  const dispatch = useDispatch();

  // USE STATE
  const [data, setMsgErrors] = React.useState([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [percentaje, setPercentaje] = React.useState(0);
  const [btnText, setBtnText] = React.useState('Crear Categoria Cotizacion');
  const [message_alert_snack, setMessageAlertSnack] = React.useState('');
  const [type_alert_snack, setTypeAlertSnack] = React.useState('success');

  const [messagesAlerts, setMessagesAlerts] = React.useState<any>([]);

  const [modalSessionExpired, setModalSessionExpired] = React.useState<boolean>(false);

  const [alert_snack, setAlertSnack] = React.useState(false);
  const [msgSnack, setMsgSnack] = React.useState({
    msg: '',
    status: 'success',
  });

  // PROPS
  const { open, handleCloseDialog, buttons = false } = props;

  // DATA MODEL
  const { formId, formField } = createTipoCotizacionFormModel;

  // VALIDATION SCHEMA
  const currentValidationSchema = validationSchema[0];

  // CONST FUNCTIONS
  const handleCloseT = () => {
    handleCloseDialog(false);
  };

  const _handleSubmit = async (values: any, actions: any) => {
    const havePermissions = await hasPemissions('modulo.thumano', 'modulo.parametros', 'create');

    if (!havePermissions) {
      await validateLsPermissions('No tienes pemisos para (Crear) parametros.');

      return false;
    }

    setIsSubmitting(true);
    setBtnText('Un momento...');

    actions.setSubmitting(true);

    const response = await dispatch(setCategoriaCotizacionesStore(values, setPercentaje));

    const result = await validateResponseCreate(response, ' (Crear Parametro) ');

    setBtnText('Crear Parametro');
    setIsSubmitting(false);

    if (!result) {
      return false;
    }

    if (response.ok) {
      const message: any = {
        msg: response.message,
        status: 'success',
      };

      await setMsgSnack(message);
      await setAlertSnack(true);

      actions.resetForm();
      handleCloseDialog(false);
    }
  };

  const validateLsPermissions = async (message: string) => {
    const errors: any = {
      msg: message,
      status: 'error',
    };

    await setMsgSnack(errors);
    await setAlertSnack(true);
  };

  const validateResponseCreate = async (response: any, textActionConcat: string) => {
    if (response.code == 401) {
      await localStorage.removeItem('lastSelectedQypht');
      await closeSessionGlobal();
      await handleCloseDialog(false);
      setModalSessionExpired(true);
      return false;
    }

    // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION
    if (!response.ok && response.code == 403) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    // 403 EL DATO YA SE ENCUENTRA REGISTRADO
    if (!response.ok && response.code == 409) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO
    if (!response.ok && response.code == 400) {
      const errors: any = [];

      response.errors.forEach((element: any) => {
        errors.push({
          type: 'error',
          text: element,
        });
      });

      setMessagesAlerts(errors);
      return false;
    }

    return true;
  };

  return (
    <React.Fragment>
      <ModalSessionExpired open={modalSessionExpired} />

      <SnackAlert
        open={alert_snack}
        onclick={setAlertSnack}
        text={msgSnack.msg}
        severity={msgSnack.status}
      />

      <BootstrapDialog
        onClose={handleCloseT}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth={true}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Crear Categoria Cotizacion
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseT}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Grid>
            <Formik
              initialValues={formInitialValues}
              validationSchema={currentValidationSchema}
              onSubmit={_handleSubmit}
            >
              <>
                <Form id={formId}>
                  <CreateCategoriaCotizacionForm formField={formField} />

                  <Button
                    disabled={isSubmitting}
                    style={{ margin: 5, float: 'right' }}
                    type="submit"
                    variant="contained"
                    color={'primary'}
                  >
                    {btnText}
                  </Button>
                </Form>
              </>
            </Formik>
          </Grid>
        </DialogContent>
        <DialogActions></DialogActions>
      </BootstrapDialog>
    </React.Fragment>
  );
};
